import {
  Fragment,
  useEffect,
  useState,
  useCallback,
  useMemo,
  useTransition,
  useRef,
  startTransition,
} from "react";
import { useNavigate, useParams } from "react-router";
import { useMutation } from "@tanstack/react-query";
import {
  Form,
  Image,
  Skeleton,
  Text,
  TextField,
  TextArea,
  DropZone,
  IllustratedMessage,
  Heading,
  Content,
  ButtonGroup,
  Button,
  FileTrigger,
  ActionButton,
  Divider,
  CloseButton,
  DialogTrigger,
  CustomDialog,
  Picker,
  PickerItem,
  ProgressCircle,
  LinkButton,
  CardView,
  Card,
  CardPreview,
  Collection,
  ActionMenu,
  MenuItem,
  SkeletonCollection,
  SegmentedControl,
  SegmentedControlItem,
} from "@react-spectrum/s2";
import { Pressable } from "react-aria-components";

import {
  useExpressTemplate,
  useGenerateVariation,
  useExportRendition,
  useExpressPollStatus,
} from "@app/hooks/expressTemplates.js";
import { uploadFileByBlob } from "@app/services/s3.js";
import * as expressSDK from "@app/services/expressSDK.js";
import { getImageBlob } from "@app/services/downloadFile.js";
import { store } from "../../store/index.js";
import { cropImageBlob, imageBlobToDataUrl } from "@app/utils/imageBlob";

// import ErrorIcon from "@react-spectrum/s2/illustrations/linear/AlertNotice";
import DropToUploadIcon from "@react-spectrum/s2/illustrations/linear/DropToUpload";
import DownloadIcon from "@react-spectrum/s2/icons/Download";
import UploadIcon from "@react-spectrum/s2/icons/Upload";
import ViewGridIcon from "@react-spectrum/s2/icons/ViewGrid";
import ViewGridFluidIcon from "@react-spectrum/s2/icons/ViewGridFluid";
import ExpressIcon from "icon:@app/assets/AdobeExpressIcon.svg";

import { style } from "@react-spectrum/s2/style" with { type: "macro" };

const metadata = import("./assets/**/index.js");

const cropImagesInTags = async (tags, taggedElements) => {
  const croppedTags = {};

  const cropPromises = tags
    .filter((tag) => tag.type === "image")
    .map(async (tag) => {
      const element = taggedElements.find((el) => el.name === tag.name);
      if (element) {
        const croppedBlob = await cropImageBlob(tag.blob, element.size);
        croppedTags[tag.name] = croppedBlob;
        croppedTags[tag.name].name =
          `${crypto.randomUUID()}.${tag.blob?.type?.split("/")?.[1] ?? "jpg"}`;
      }
    });

  await Promise.all(cropPromises);

  return croppedTags;
};

export const TemplateView = () => {
  const params = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    if (!params.id) {
      navigate("/templates");
    }
  }, [params.id, navigate]);

  if (!params.id) {
    return null;
  }

  const { data, isLoading, isFetching, hasNextPage, fetchNextPage, isError } =
    useExpressTemplate(params.id);

  const template = useMemo(() => {
    return (data?.pages || []).reduce((acc, { id, name, documentPages }) => {
      acc.id = id;
      acc.name = name;
      acc.documentPages = [
        ...(acc.documentPages || []),
        ...(documentPages || []),
      ];
      return acc;
    }, {});
  }, [data]);

  useEffect(() => {
    if (!isFetching && hasNextPage) {
      fetchNextPage();
    }
  }, [isFetching, hasNextPage, fetchNextPage]);

  useEffect(() => {
    (async () => {
      const ccEverywhere = await expressSDK.initialize();
      ccEverywhere.editor?.warmup({
        intent: "create-with-asset",
        mode: "minimal",
      });
    })();
  }, []);

  const [meta, setMeta] = useState(null);
  const [isLoadingMeta, startLoadingMetaTransition] = useTransition();
  useEffect(() => {
    startLoadingMetaTransition(async () => {
      try {
        const meta = metadata[params.id.replaceAll(":", "_")];
        setMeta(meta.default);
      } catch (e) {
        setMeta(null);
      }
    });
  }, [params.id]);

  const loadingState = isError
    ? "error"
    : isLoading || isLoadingMeta
      ? "loading"
      : "idle";

  const [tags, setTags] = useState({});

  const taggedElements = useMemo(() => {
    if (!data?.pages?.length) return [];

    const _taggedElements =
      (template.documentPages?.flatMap((doc) => doc.taggedElements) || [])
        ?.filter((tag) => !/#\d+$/.test(tag.name))
        .filter(
          (tag, index, self) =>
            index === self.findIndex((t) => t.name === tag.name),
        )
        .map((tag) => ({ id: crypto.randomUUID(), ...tag })) || [];

    if (meta) {
      for (const [key, config] of Object.entries(meta)) {
        const tag = _taggedElements.find((t) => t.name === key);
        if (tag) {
          if (config.value instanceof URL) {
            tag.url = config.value.href;
          } else if (typeof config.value === "string") {
            tag.defaultValue = config.value;
          }

          if (config.objectFit) {
            tag.objectFit = config.objectFit;
          }
        }
      }
    }

    if (tags) {
      for (const [key, value] of Object.entries(tags)) {
        const tag = _taggedElements.find((t) => t.name === key);
        if (tag) {
          Object.assign(tag, value);
        }
      }
    }

    return _taggedElements;
  }, [template, meta, tags]);

  const taggedImages = useMemo(
    () => taggedElements.filter((el) => el.type === "image"),
    [taggedElements],
  );

  const [variationName, setVariationName] = useState(
    template?.name + " — variation",
  );
  const [variationFormat, setVariationFormat] = useState("video/mp4");

  useEffect(() => {
    if (template?.name) {
      setVariationName(template.name + " — variation");
    }
  }, [template?.name]);

  const handleTagChange = useCallback((elementName, value) => {
    setTags((prevTags) => {
      const newTags = { ...prevTags };

      newTags[elementName] = value;

      return newTags;
    });
  }, []);

  const fileTriggerRefs = useRef({});

  const handleFileSelect = useCallback((itemId) => {
    if (fileTriggerRefs.current[itemId]?.value) {
      fileTriggerRefs.current[itemId].value = "";
    }
    fileTriggerRefs.current[itemId]?.click();
  }, []);

  const handleDrop = useCallback((filesPromises, elementName) => {
    startTransition(async () => {
      const files = await Promise.all(filesPromises);

      if (files && files.length > 0) {
        const file = files[0];

        if (file) {
          const blobUrl = await imageBlobToDataUrl(files[0]);

          setTags((prevTags) => {
            const newTags = { ...prevTags };

            newTags[elementName] = file;
            newTags[elementName].url = blobUrl;

            return newTags;
          });
        }
      }
    });
  }, []);

  const handleEditImage = useCallback((element) => {
    startTransition(async () => {
      const ccEverywhere = await expressSDK.initialize();

      console.log({ ccEverywhere, element });

      const { blob: imageBlob } = await getImageBlob(element);
      const imageDataUrl = await imageBlobToDataUrl(imageBlob);

      const docConfig = element?.documentId
        ? { documentId: element.documentId }
        : {
            canvasSize: {
              width: element.size.width,
              height: element.size.height,
              unit: "px",
            },
            asset: {
              type: "image",
              // dataType: "blob",
              // data: imageBlob,
              dataType: "base64",
              data: imageDataUrl,
              name: element.name,
            },
          };

      const appConfig = {
        colorTheme: store.getState().colorScheme,
        spectrumTheme: "spectrum-two",
        callbacks: {
          onPublish: async (intent, publishParams) => {
            const asset = publishParams.asset[0];
            const documentId = publishParams.documentId;

            console.log({ intent, publishParams });

            const blob = await (await fetch(asset.data)).blob();
            blob.url = asset.data;
            blob.documentId = documentId;

            setTags((prevTags) => {
              const newTags = { ...prevTags };
              newTags[element.name] = blob;

              // Object.assign(newTags[element.name], {
              //   url: asset.data,
              //   documentId,
              // });

              return newTags;
            });
          },
          onError: (error) => {},
        },
        selectedCategory: "hidden",
        multiPage: false,
        allowedFileTypes: ["image/png", "image/jpeg", "video/mp4"],
      };

      const exportConfig = [
        {
          id: "save-asset",
          label: "Save to BeStory",
          action: {
            target: "publish",
            outputType: "blob",
            publishFileType: "image/png",
          },
          // action: { target: "download" },
          style: { uiType: "button" },
        },
      ];

      const editorAction = element?.documentId
        ? ccEverywhere.editor.edit
        : ccEverywhere.editor.createWithAsset;

      editorAction.call(
        ccEverywhere.editor,
        docConfig,
        appConfig,
        exportConfig,
      );
    });
  }, []);

  const handleExpressQuickAction = useCallback((actionType, element) => {
    startTransition(async () => {
      const ccEverywhere = await expressSDK.initialize();

      console.log({ ccEverywhere, element });

      const { blob: imageBlob } = await getImageBlob(element);
      const imageDataUrl = await imageBlobToDataUrl(imageBlob);

      const docConfig = {
        canvasSize: {
          width: element.size.width,
          height: element.size.height,
          unit: "px",
        },
        asset: {
          type: "image",
          // dataType: "blob",
          // data: imageBlob,
          dataType: "base64",
          data: imageDataUrl,
          name: element.name,
        },
      };

      const appConfig = {
        colorTheme: store.getState().colorScheme,
        spectrumTheme: "spectrum-two",
        callbacks: {
          onPublish: async (intent, publishParams) => {
            const asset = publishParams.asset[0];
            const documentId = publishParams.documentId;

            console.log({ intent, publishParams });

            const blob = await (await fetch(asset.data)).blob();
            blob.url = asset.data;
            blob.documentId = documentId;

            setTags((prevTags) => {
              const newTags = { ...prevTags };
              newTags[element.name] = blob;

              // Object.assign(newTags[element.name], {
              //   url: asset.data,
              //   documentId,
              // });

              return newTags;
            });
          },
          onError: (error) => {},
        },
        selectedCategory: "hidden",
        multiPage: false,
        allowedFileTypes: ["image/png", "image/jpeg", "video/mp4"],
      };

      const exportConfig = [
        {
          id: "save-asset",
          label: "Save to BeStory",
          action: {
            target: "publish",
            outputType: "blob",
            publishFileType: "image/png",
          },
          // action: { target: "download" },
          style: { uiType: "button" },
        },
      ];

      switch (actionType) {
        case "crop":
          ccEverywhere.quickAction.cropImage(
            docConfig,
            appConfig,
            exportConfig,
          );
          break;
        default:
          break;
      }
    });
  }, []);

  const isDownloadDisabled = Object.values(tags).filter(Boolean).length === 0;

  const generateVariation = useGenerateVariation();
  const exportRendition = useExportRendition();
  const pollStatus = useExpressPollStatus();
  const uploadFile = useMutation({
    mutationFn: ({ filePath, fileBlob }, { signal }) =>
      uploadFileByBlob(filePath, fileBlob, { signal }),
    meta: { errorMessage: "Failed to upload file. Please try again." },
  });

  const { reset: resetFileUpload } = uploadFile;

  const resetAll = useCallback(() => {
    resetFileUpload();
    generateVariation.reset();
    exportRendition.reset();
    pollStatus.reset();
  }, [resetFileUpload, generateVariation, exportRendition, pollStatus]);

  const [variationDoc, setVariationDoc] = useState({});

  const status = uploadFile.isPending
    ? "Uploading assets…"
    : generateVariation.isPending
      ? "Generating variation…"
      : generateVariation.isSuccess &&
          pollStatus.isPending &&
          pollStatus.variables.jobId === generateVariation.data.jobId
        ? "Creating new document…"
        : exportRendition.isPending
          ? "Exporting rendition…"
          : exportRendition.isSuccess &&
              pollStatus.isPending &&
              pollStatus.variables.jobId === exportRendition.data.jobId
            ? "Generating rendition…"
            : "Done";

  const handleGenerateVariation = useCallback(
    (callback) => {
      startTransition(async () => {
        const id = crypto.randomUUID();
        const ldap = store.getState().ims?.profile?.email.split("@")[0];
        const folderPath = `${ldap}/express-templates/${template.id}/${id}`;

        const allTaggedElements = template?.documentPages?.flatMap(
          (doc) => doc.taggedElements,
        );

        const tagsToCrop = Object.entries(tags)
          .filter(
            ([key, value]) =>
              value instanceof Blob &&
              taggedElements.some(
                (el) => el.name === key && el?.objectFit === "cover",
              ),
          )
          .flatMap(([key, blob]) => {
            const elements = allTaggedElements
              .filter((tag) => new RegExp(`^${key}(\\s#\\d+)?$`).test(tag.name))
              ?.map((tag) => Object.assign(tag, { blob }));

            return elements;
          });

        const croppedTags = await cropImagesInTags(
          tagsToCrop,
          allTaggedElements,
        );

        const imageTagsPromises = Object.entries({ ...tags, ...croppedTags })
          .filter(([_, value]) => value instanceof Blob)
          .map(([key, file]) =>
            Promise.all([
              key,
              uploadFile
                .mutateAsync({
                  filePath: `${folderPath}/${file.name}`,
                  fileBlob: file,
                })
                .then(({ presignURL }) => presignURL),
            ]),
          );

        const imageTags = await Promise.all(imageTagsPromises);
        const textTags = Object.entries(tags)
          .filter(
            ([_, { value }]) =>
              typeof value === "string" && value.trim().length > 0,
          )
          .map(([key, { value }]) => [key, value.trim()]);

        const allTags = [...imageTags, ...textTags];

        if (allTags.length === 0) {
          return;
        }

        const tagMappings = allTags.reduce((acc, [key, value]) => {
          acc[key] = value;
          return acc;
        }, {});

        template?.documentPages?.forEach((page) => {
          page.taggedElements?.forEach((element) => {
            if (/\s#\d+$/.test(element.name)) {
              const baseName = element.name.replace(/\s#\d+$/, "");

              if (!tagMappings[element.name] && tagMappings[baseName]) {
                tagMappings[element.name] = tagMappings[baseName];
              }
            }
          });
        });

        const variationPayload = {
          id: template.id,
          variationDetails: {
            pages: "1-", // all pages
            // projectId: null,
            preferredDocumentName: variationName,
            tagMappings,
          },
        };

        const variationResult =
          await generateVariation.mutateAsync(variationPayload);

        const variationStatus = await pollStatus.mutateAsync({
          jobId: variationResult.jobId,
        });

        setVariationDoc(variationStatus.document);

        // const exportPayload = {
        //   id: variationStatus.document.id,
        //   pages: "1",
        //   options: {
        //     format: variationFormat,
        //     // size: 1920
        //   },
        // };

        // const exportResult = await exportRendition.mutateAsync(exportPayload);

        // const exportStatus = await pollStatus.mutateAsync({
        //   jobId: exportResult.jobId,
        // });

        callback?.(variationResult);
      });
    },
    [tags, taggedElements, template, variationName, variationFormat],
  );

  const [renditionUrl, setRenditionUrl] = useState(null);

  const handleExportRendition = useCallback(
    (callback) => {
      startTransition(async () => {
        const exportPayload = {
          id: variationDoc.id,
          pages: "1",
          options: {
            format: variationFormat,
            // size: 1920
          },
        };

        const exportResult = await exportRendition.mutateAsync(exportPayload);

        const exportStatus = await pollStatus.mutateAsync({
          jobId: exportResult.jobId,
        });

        setRenditionUrl(exportStatus.data.pageRenditionsResult[0].renditionUrl);

        // callback?.(variationResult);
      });
    },
    [variationDoc, variationFormat],
  );

  const [galleryLayout, setGalleryLayout] = useState("grid");

  return (
    <div
      className={style({
        display: "grid",
        gridTemplateRows: ["auto", "1fr", "auto"],
        size: "full",
        maxWidth: 1800,
        marginX: "auto",
        // paddingStart: "[calc((100% - 1200px) / 2)]",
        boxSizing: "border-box",
        gap: 12,
        paddingTop: 24,
      })}
    >
      <Skeleton isLoading={isLoading}>
        <div className={style({ font: "title-xl" })}>
          <Text>{template?.name ?? "Loading template Name …"}</Text>
        </div>
      </Skeleton>
      <div className={style({ display: "flex", gap: 24, overflow: "hidden" })}>
        <Skeleton isLoading={isLoading}>
          <div
            className={style({
              display: "flex",
              flexDirection: "column",
              flexBasis: 640,
              gap: 24,
              size: "full",
              minWidth: 320,
              boxSizing: "border-box",
              overflowY: "auto",
              // paddingEnd: 24,
            })}
          >
            {template?.documentPages?.map((page, idx) => (
              <Fragment key={page.pageNumber}>
                {idx !== 0 && (
                  <Divider
                    styles={style({ gridColumnStart: 1, gridColumnEnd: -1 })}
                  />
                )}
                <div
                  className={style({
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                  })}
                >
                  <Image
                    src={page.thumbnailUrl}
                    alt={page?.pageTitle || `Page ${page.pageNumber}`}
                    styles={style({
                      size: 72,
                      aspectRatio: "1/1",
                      objectFit: "contain",
                      borderRadius: "lg",
                    })}
                  />
                  <div className={style({ font: "title" })}>
                    {page?.pageTitle || `Page ${page.pageNumber}`}{" "}
                    <Text
                      styles={style({
                        font: "detail",
                        whiteSpace: "nowrap",
                      })}
                    >
                      ({`${page.size.width} × ${page.size.height}`})
                    </Text>
                  </div>
                </div>
                <Form
                  isDisabled={generateVariation.isPending}
                  styles={style({ marginTop: 8 })}
                >
                  {taggedElements
                    // ?.filter((el, i, arr) => arr.indexOf(el) === i)
                    ?.filter(
                      (el) =>
                        el.type !== "image" &&
                        page.taggedElements?.some(
                          (taggedEl) => taggedEl.name === el.name,
                        ),
                    )
                    ?.map((element, edx) => {
                      const key = `${element.type}-${element.name}}`;
                      if (element.type === "text") {
                        const TextComponent =
                          element.name.length > 40 ? TextArea : TextField;
                        return (
                          <div key={key}>
                            <Text
                              styles={style({
                                display: "flex",
                                font: "ui",
                                color: "neutral-subdued",
                                marginBottom: 8,
                              })}
                            >
                              <span>
                                {element.name}
                                {!!element?.defaultValue && (
                                  <>
                                    {" "}
                                    <Text
                                      styles={style({
                                        font: "ui-xs",
                                        color: "neutral-subdued",
                                      })}
                                    >
                                      (
                                      {`${element.defaultValue.length} characters`}
                                      )
                                    </Text>
                                  </>
                                )}
                              </span>

                              <Text
                                styles={style({
                                  marginStart: "auto",
                                  font: "ui-xs",
                                  color: "neutral-subdued",
                                })}
                              >
                                {`${tags?.[element.name]?.value?.length ?? 0} characters`}
                              </Text>
                            </Text>
                            <TextArea
                              aria-label={element.name}
                              value={tags[element.name]?.value ?? ""}
                              placeholder={element?.defaultValue ?? ""}
                              onChange={(value) =>
                                handleTagChange(element.name, { value })
                              }
                            />
                          </div>
                        );
                      }

                      return null;
                    })}
                </Form>
              </Fragment>
            ))}
            {isLoading &&
              new Array(2).fill(null).map((_, index) => (
                <Fragment key={index}>
                  <div
                    className={style({
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                    })}
                  >
                    <Image
                      alt="Loading"
                      styles={style({
                        size: 72,
                        aspectRatio: "1/1",
                        objectFit: "contain",
                        marginTop: 12,
                        borderRadius: "lg",
                      })}
                    />
                    <Text styles={style({ font: "title" })}>Loading</Text>
                  </div>
                  <div
                    className={style({
                      display: "flex",
                      flexDirection: "column",
                      gap: 24,
                    })}
                  >
                    {new Array(3).fill(null).map((_, index) => (
                      <div key={index}>
                        <div
                          className={style({
                            font: "detail",
                            marginBottom: 8,
                          })}
                        >
                          <Text>Loading...</Text>
                        </div>
                        <div>
                          <Text
                            styles={style({
                              font: "body-xl",
                            })}
                          >
                            Loading input tag...
                          </Text>
                        </div>
                      </div>
                    ))}
                  </div>
                </Fragment>
              ))}
          </div>
        </Skeleton>
        <div
          className={style({
            display: "flex",
            flexDirection: "column",
            gap: 12,
            size: "full",
            // flexBasis: "auto",
            // flexShrink: 1,
          })}
        >
          <SegmentedControl
            aria-label="Gallery layout"
            isDisabled={!taggedImages.length}
            onSelectionChange={setGalleryLayout}
            styles={style({ marginStart: "auto" })}
          >
            <SegmentedControlItem id="grid" aria-label="Grid">
              <ViewGridIcon />
            </SegmentedControlItem>
            <SegmentedControlItem id="waterfall" aria-label="Waterfall">
              <ViewGridFluidIcon
                UNSAFE_className={style({ transform: "rotate(90deg)" })}
              />
            </SegmentedControlItem>
          </SegmentedControl>
          <CardView
            layout={galleryLayout}
            selectionMode="none"
            variant="quiet"
            loadingState={loadingState}
            styles={style({
              size: "full",
            })}
          >
            <Collection items={taggedImages} dependencies={[galleryLayout]}>
              {(item) => (
                <Card textValue={item.name}>
                  <CardPreview>
                    <DropZone
                      onDrop={(event) =>
                        handleDrop(
                          event.items.map((item) => item.getFile()),
                          item.name,
                        )
                      }
                      UNSAFE_className={style({ padding: 4 })}
                    >
                      <FileTrigger
                        ref={(ref) => (fileTriggerRefs.current[item.id] = ref)}
                        onSelect={(files) => handleDrop(files, item.name)}
                      >
                        <Pressable tabIndex={1}>
                          <Image
                            src={item?.url}
                            alt={item.name}
                            styles={style({
                              borderRadius: "sm",
                              objectFit: "contain",
                            })}
                            UNSAFE_style={
                              galleryLayout === "waterfall"
                                ? {
                                    aspectRatio: `${item?.size?.width} / ${item?.size?.height}`,
                                  }
                                : {}
                            }
                            renderError={() => (
                              <div
                                className={style({
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  size: "full",
                                })}
                              >
                                <DropToUploadIcon
                                  styles={style({ size: 48 })}
                                />
                              </div>
                            )}
                          />
                        </Pressable>
                      </FileTrigger>
                    </DropZone>
                  </CardPreview>
                  <Content>
                    <Text slot="title">
                      <Text styles={style({ marginEnd: 8 })}>{item.name} </Text>
                      <Text
                        styles={style({
                          font: "ui-sm",
                          color: "neutral-subdued",
                          whiteSpace: "nowrap",
                        })}
                      >{`${parseInt(item?.size?.width, 10)} × ${parseInt(item?.size?.height, 10)} px`}</Text>
                    </Text>
                    <ActionMenu>
                      <MenuItem
                        textValue="Edit in Express"
                        onPress={() => handleEditImage(item)}
                      >
                        <ExpressIcon />
                        <Text>Edit in Express</Text>
                      </MenuItem>
                      {item?.url && !item?.documentId && (
                        <MenuItem
                          textValue="Crop image"
                          onPress={() => handleExpressQuickAction("crop", item)}
                        >
                          <ExpressIcon />
                          <Text>Crop image</Text>
                        </MenuItem>
                      )}
                      <MenuItem
                        textValue="Browse files"
                        onPress={() => handleFileSelect(item.id)}
                      >
                        <UploadIcon />
                        <Text>Browse files</Text>
                      </MenuItem>
                    </ActionMenu>
                  </Content>
                </Card>
              )}
            </Collection>
            {loadingState === "loading" && (
              <SkeletonCollection>
                {() => (
                  <Card>
                    <CardPreview>
                      <Image
                      // UNSAFE_style={{
                      //   height: [50, 200, 400][Math.floor(Math.random() * 3)],
                      // }}
                      />
                    </CardPreview>
                    <Content>
                      <Text slot="title">Loading...</Text>
                      <Text slot="description">Loading description...</Text>
                    </Content>
                  </Card>
                )}
              </SkeletonCollection>
            )}
          </CardView>
        </div>
      </div>
      <ButtonGroup styles={style({ marginTop: 12, marginStart: "auto" })}>
        <DialogTrigger>
          <Button
            isDisabled={isDownloadDisabled}
            variant="accent"
            onPress={() => {
              resetAll();
            }}
          >
            <Text>Generate rendition</Text>
          </Button>
          <CustomDialog size="L">
            {({ close }) => (
              <div
                className={style({
                  display: "flex",
                  flexDirection: "column",
                  gap: 24,
                })}
              >
                {/* <pre>
                  {JSON.stringify(
                    {
                      uploadFile,
                      generateVariation,
                      exportRendition,
                      pollStatus,
                    },
                    null,
                    2,
                  )}
                </pre> */}
                {uploadFile.isIdle &&
                  generateVariation.isIdle &&
                  exportRendition.isIdle &&
                  pollStatus.isIdle && (
                    <>
                      <Heading
                        slot="title"
                        styles={style({
                          font: "heading",
                          marginY: 0,
                        })}
                      >
                        Generate rendition
                      </Heading>
                      <Form>
                        <TextField
                          label="Document name"
                          value={variationName}
                          onChange={setVariationName}
                        />
                        {exportRendition.isSuccess && pollStatus.isSuccess && (
                          <Picker
                            label="File format"
                            value={variationFormat}
                            onChange={setVariationFormat}
                          >
                            <PickerItem id="video/mp4">
                              MP4 (Best for video)
                            </PickerItem>
                            <PickerItem id="image/png">
                              PNG (Best for images)
                            </PickerItem>
                            <PickerItem id="image/jpeg">
                              JPG (Best for small file sizes)
                            </PickerItem>
                          </Picker>
                        )}

                        {/* TODO: add size picker */}
                      </Form>
                      <ButtonGroup
                        styles={style({
                          marginStart: "auto",
                        })}
                      >
                        <Button
                          variant="secondary"
                          onPress={() => {
                            resetAll();
                            close();
                          }}
                        >
                          Cancel
                        </Button>
                        <Button
                          variant="accent"
                          onPress={() => handleGenerateVariation()}
                        >
                          Generate
                        </Button>
                      </ButtonGroup>
                    </>
                  )}
                {(uploadFile.isPending ||
                  generateVariation.isPending ||
                  (pollStatus.isPending &&
                    pollStatus.variables.jobId ===
                      generateVariation.data.jobId)) && (
                  <div
                    className={style({
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 24,
                      marginY: 64,
                    })}
                  >
                    <ProgressCircle
                      isIndeterminate
                      aria-label={status}
                      size="M"
                    />
                    <Text styles={style({ font: "detail" })}>{status}</Text>
                  </div>
                )}
                {generateVariation.isSuccess && pollStatus.isSuccess && (
                  <>
                    <Content
                      styles={style({
                        display: "flex",
                        flexDirection: "column",
                        gap: 12,
                        alignItems: "center",
                      })}
                    >
                      <Image
                        src={variationDoc?.thumbnailUrl}
                        alt={variationDoc?.name}
                        styles={style({
                          width: 180,
                          borderRadius: "default",
                        })}
                      />
                      <Text styles={style({ font: "title-sm" })}>
                        {variationDoc?.name.replace(".express", "")}
                      </Text>
                      <LinkButton
                        variant="secondary"
                        href={`https://new.express.adobe.com/id/${variationDoc?.id}`}
                        target="_blank"
                        isDisabled={!variationDoc?.id}
                      >
                        <ExpressIcon />
                        <Text>Open in Express</Text>
                      </LinkButton>
                    </Content>
                    <ButtonGroup
                      isDisabled={
                        exportRendition.isPending || pollStatus.isPending
                      }
                      styles={style({ marginStart: "auto" })}
                    >
                      <Button
                        variant="secondary"
                        fillStyle="outline"
                        onPress={() => {
                          resetAll();
                          close();
                        }}
                      >
                        Close
                      </Button>
                      {!exportRendition.isSuccess && (
                        <Button
                          variant="accent"
                          isPending={
                            exportRendition.isPending || pollStatus.isPending
                          }
                          onPress={() => handleExportRendition()}
                        >
                          Export rendition
                        </Button>
                      )}
                      {exportRendition.isSuccess && (
                        <Button
                          variant="accent"
                          isPending={
                            exportRendition.isPending || pollStatus.isPending
                          }
                          isDisabled={!renditionUrl}
                          onPress={() => {
                            window.open(renditionUrl, "_blank");

                            // resetAll();
                            // close();
                          }}
                        >
                          <DownloadIcon />
                          <Text>Download rendition</Text>
                        </Button>
                      )}
                    </ButtonGroup>
                  </>
                )}
              </div>
            )}
          </CustomDialog>
        </DialogTrigger>
      </ButtonGroup>
    </div>
  );
};

TemplateView.propTypes = {};
